<template>
    <div>
        <!-- First name input field -->
        <div class="input" :class="getInputClass('first_name')">
            <label class="input__label" for="account-first-name">
                {{ $t('ACCOUNT_FORM_FIRST_NAME') }}
            </label>
            <input
                    id="account-first-name"
                    type="text"
                    class="input__field"
                    v-model.lazy="account.firstName">
            <!-- Validation -->
            <p class="input__message" v-if="$v.account.firstName.$error"
               v-text="$t('EDIT_ACCOUNT_DETAILS_PLEASE')"></p>
            <p class="input__message"
               v-if="errorHandler.has('first_name')"
               v-text="errorHandler.get('first_name')">
            </p>
        </div>

        <!-- Last name input field -->
        <div class="input" :class="getInputClass('last_name')">
            <label class="input__label" for="account-last-name">
                {{ $t('ACCOUNT_FORM_LAST_NAME') }}
            </label>
            <input
                    id="account-last-name"
                    type="text"
                    class="input__field"
                    v-model.lazy="account.lastName">
            <!-- Validation -->
            <p class="input__message" v-if="$v.account.lastName.$error"
               v-text="$t('EDIT_ACCOUNT_DETAILS_PLEASE_LAST')"></p>
            <p class="input__message"
               v-if="errorHandler.has('last_name')"
               v-text="errorHandler.get('last_name')">
            </p>
        </div>

        <!-- Email field -->
        <legacy-email-address-field
                class="contentsquare_sensitive_data"
                v-model="account.email"
                :validation.sync="formValidations.email">
        </legacy-email-address-field>

        <!-- Password input field -->
        <password-field
                class="contentsquare_sensitive_data"
                v-if="showPasswordReset"
                v-model="account.password"
                :message="errorHandler.get('password')"
                :require-strong-password="true"
                :is-valid="!errorHandler.has('password')">
        </password-field>

        <!--Reset password toggle-->
        <div class="w-full px-3 -mt-1 mb-4">
            <a class="link" :class="{ 'text-xs': showPasswordReset}"
               @click.prevent="togglePasswordReset"
               v-text="passwordResetToggleCopy" v-html="$t('EDIT_ACCOUNT_DETAILS_RESET')">
            </a>
        </div>

        <!-- Telephone input field -->
        <telephone-field
                v-model="account.telephone"
                :validation.sync="formValidations.telephone">
        </telephone-field>

        <!--Opt In-->
        <div class="input mt-6" :class="getInputClass('opt_in')">
            <div class="input__checkbox">
                <input
                        id="account-opt-in"
                        type="checkbox"
                        class="input__checkbox-item"
                        v-model="account.optIn">
                <label for="account-opt-in" class="input__checkbox-label"
                       v-html="$t('EDIT_ACCOUNT_DETAILS_I_LIKE')">
                </label>
            </div>
            <p class="input__message"
               v-if="errorHandler.has('opt_in')"
               v-text="errorHandler.get('opt_in')">
            </p>
        </div>

        <!--Weekly email-->
        <div class="input" :class="getInputClass('weekly_email')">
            <div class="input__checkbox">
                <input
                        id="account-weekly-email"
                        type="checkbox"
                        class="input__checkbox-item"
                        v-model="account.weeklyEmail">
                <label for="account-weekly-email" class="input__checkbox-label">
                    I’d like a weekly spoiler email so I know what’s coming up.
                </label>
            </div>
            <p class="input__message"
               v-if="errorHandler.has('weekly_email')"
               v-text="errorHandler.get('weekly_email')">
            </p>
        </div>

        <!--Confirm changes button-->
        <div class="text-center">
            <p class="text-center py-2" v-html="message"></p>
            <button
                    class="btn btn--primary btn--auto-width"
                    @click="submit"
                    :disabled="formProcessing" v-html="$t('EDIT_ACCOUNT_DETAILS_SAVE')">
            </button>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/src/validators';
import { convertToCamelCase } from '@/helpers/StringHelpers';
import UserService from '@/services/users/UserService';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import PasswordField from '@/components/forms/fields/PasswordField';
import TelephoneField from '@/components/forms/fields/LegacyTelephoneField';
import LegacyEmailAddressField from '@/components/forms/fields/LegacyEmailAddressField';

let userService = new UserService();

export default {
    name: 'EditAccountDetailsForm',
    data() {
        return {
            account: {
                firstName: null,
                lastName: null,
                email: null,
                password: null,
                telephone: null,
                optIn: false,
                weeklyEmail: false,
            },
            formValidations: {
                email: false,
                telephone: false,
            },
            showPasswordReset: false,
            errorHandler: new ErrorResponseHandler(),
            formProcessing: false,
            message: '',
        };
    },
    mounted() {
        this.initialiseForm();
    },
    methods: {
        /**
         * Handle the submit click event.
         *
         * @returns {void}
         */
        submit() {
            this.toggleProcessing();
            this.errorHandler.setResponse({});
            this.validateInput();
        },

        /**
         * Handle updating the authenticated user's details via the API.
         *
         * @returns {void}
         */
        handleUpdatingAccountDetailsViaApi() {
            let payload = {
                ...this.account,
                password: this.showPasswordReset ? this.account.password : null,
            };

            userService.updateUser(payload)
                .then(() => {
                    this.message = this.$t('EDIT_ACCOUNT_DETAILS_ACCOUNT_UPDATED');
                    this.initialiseForm();
                })
                .catch(error => {
                    this.errorHandler.setResponse(error.response.data);
                    this.toggleProcessing();
                });
        },

        /**
         * Toggle showing the password reset field.
         *
         * @return {void}
         */
        togglePasswordReset() {
            this.showPasswordReset = !this.showPasswordReset;
        },

        /**
         * Handle updating the account user vuex and populating the form based on this.
         *
         * @return {void}
         */
        initialiseForm() {
            this.$store.dispatch('account/fetchUserData', true)
                .then(() => {
                    const user = this.user;
                    this.account.firstName = user.first_name;
                    this.account.lastName = user.last_name;
                    this.account.email = user.email;
                    this.account.telephone = user.telephone;
                    this.account.optIn = user.opt_in;
                    this.account.weeklyEmail = user.weekly_email;
                });

            this.formProcessing = false;
        },

        /**
         * Toggle the form processing state.
         *
         * @returns {void}
         */
        toggleProcessing() {
            this.formProcessing = !this.formProcessing;
        },

        /**
         * Generate the input class for the given form field.
         *
         * @param {string} fieldName
         *
         * @returns {string}
         */
        getInputClass(fieldName) {
            const key = convertToCamelCase(fieldName);
            const validationError = this.$v.account[key] && this.$v.account[key].$error;

            return this.errorHandler.has(fieldName) || validationError ? 'input--invalid' : '';
        },

        /**
         * Validate input before allowing user to update the address.
         *
         * @returns {void}
         */
        validateInput() {
            this.message = '';
            this.$v.$touch();
            this.areAllFormFieldsValid ? this.handleUpdatingAccountDetailsViaApi() : this.handleInvalidForm();
        },

        /**
         * Scroll to the top of the form for the user to correct the invalid fields.
         *
         * @returns {void}
         */
        handleInvalidForm() {
            if (this.showPasswordReset && this.account.password === null) this.account.password = '';

            this.$scrollTo('#account-first-name');
            this.toggleProcessing();
        },
    },
    computed: {
        areAllFormFieldsValid() {
            return !this.$v.$invalid;
        },
        user() {
            return this.$store.getters['account/getUser'];
        },
        passwordResetToggleCopy() {
            return this.showPasswordReset ? this.$t('EDIT_ACCOUNT_DETAILS_CANCEL') : this.$t('EDIT_ACCOUNT_DETAILS_RESET');
        },
    },
    validations: {
        account: {
            firstName: {
                required,
            },
            lastName: {
                required,
            },
            email: {
                isValid() {
                    return this.formValidations.email === true;
                },
            },
            telephone: {
                isValid() {
                    return this.formValidations.telephone === true;
                },
            },
        },
    },
    components: {
        PasswordField,
        LegacyEmailAddressField,
        TelephoneField,
    },
};
</script>
